import { HttpClient } from '@angular/common/http';
import { Injectable, Inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ACLService } from '@delon/acl';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { MenuService, SettingsService, TitleService } from '@delon/theme';
import { environment } from '@env/environment';
import { PlatformMenuEntry } from '@env/interface';
import { TranslateService } from '@ngx-translate/core';
import { concat } from 'lodash';
import { NzIconService } from 'ng-zorro-antd/icon';
import { Observable, of, combineLatest, BehaviorSubject, NEVER, Subject, merge } from 'rxjs';
import { bufferTime, catchError, filter, map, mergeMap, startWith, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { DocumentClassSortFields, Organization, OrganizationRole, PlatformRole, SortDirection } from 'src/app/graphql/data-graphql';
import { DocumentSortFields, DocumentState, Scope } from 'src/app/graphql/frontend-data-graphql';
import { AuthService, CategoryMenuEntry } from 'src/app/shared/services/auth.service';
import { DocumentClassService } from 'src/app/shared/services/document-class.service';
import { DocumentDataSource } from 'src/app/shared/services/document-data-source';
import { DocumentService } from 'src/app/shared/services/document.service';
import { OrganizationService } from 'src/app/shared/services/organization.service';

import { ICONS } from '../../../style-icons';
import { ICONS_AUTO } from '../../../style-icons-auto';
import { AppState } from '../app.state';

/**
 * Used for application startup
 * Generally used to get the basic data of the application, like: Menu Data, User Data, etc.
 */
@Injectable()
export class StartupService {
  constructor(
    iconSrv: NzIconService,
    private menuService: MenuService,
    private settingService: SettingsService,
    private aclService: ACLService,
    private titleService: TitleService,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService,
    private httpClient: HttpClient,
    private router: Router,
    private translateService: TranslateService,
    private auth: AuthService,
    private injector: Injector,
    private documentClassService: DocumentClassService,
    private documentService: DocumentService
  ) {
    iconSrv.addIcon(...ICONS_AUTO, ...ICONS);
  }

  frontend_category_settings$ = new BehaviorSubject<Record<string, CategoryMenuEntry>>({});

  public initialize(): Observable<void> {
    const token = this.tokenService.get()?.token;
    const app: AppState = {
      name: `lector.ai`,
      description: this.translateService.instant('BRAND.subtitle')
    };

    // Application information: including site name, description, year
    this.settingService.setApp({ ...this.settingService.app, ...app });

    const orgaService = this.injector.get(OrganizationService);
    orgaService
      .getById(this.auth.organization?.identifier ?? 'Unknown')
      .pipe(
        map(res => res.data.organizationByIdentifier as Organization),
        map(org => {
          return (
            (org.frontend_category_settings as Record<string, CategoryMenuEntry>) ?? {
              classification: {
                label: 'Inbox',
                icon: 'partition',
                link: 'classification'
                // Example regex
                // regex: '.*'
                // regex: `/root/letter/page_classification/[^\/]*$`
                // regex: '/root$'
              }
            }
          );
        })
      )
      .subscribe(settings => this.frontend_category_settings$.next(settings));
    combineLatest([
      this.translateService.onLangChange.pipe(startWith(this.translateService.currentLang)),
      this.auth.organization$,
      this.frontend_category_settings$
    ]).subscribe(([lang, org, frontend_category_settings]) => {
      this.menuService.clear();
      this.menuService.add([
        {
          text: this.translateService.instant('SIDEBAR.dashboard'),
          link: '/dashboard',
          group: false,
          children: [
            {
              text: this.translateService.instant('SIDEBAR.dashboard'),
              link: '/dashboard',
              icon: { type: 'icon', value: 'home' }
            }
          ]
        },
        {
          text: this.translateService.instant('SIDEBAR.documents'),
          link: '/documents',
          icon: { type: 'icon', value: 'form' },
          children: [
            {
              text: this.translateService.instant('SIDEBAR.overview'),
              link: '/documents',
              icon: { type: 'icon', value: 'database' },
              key: 'overview'
            },
            ...Object.entries(frontend_category_settings ?? {}).map(([k, e]) => ({
              text: e.label,
              link: `/documents/inbox/${k}`,
              icon: { type: 'icon', value: e.icon } as any,
              key: e.regex ?? 'inbox',
              acl:
                e.platform_roles || e.organization_roles
                  ? {
                      role: concat((e.platform_roles as string[] | undefined) || [], (e.organization_roles as string[] | undefined) || [])
                    }
                  : undefined
            }))
          ]
        },
        {
          text: this.translateService.instant('SIDEBAR.administration'),
          icon: { type: 'icon', value: 'setting' },
          acl: {
            role: [PlatformRole.Developer, PlatformRole.PlatformAdmin, OrganizationRole.OrganizationAdmin]
          },

          children: [
            {
              text: this.translateService.instant('SIDEBAR.users'),
              link: '/admin/users',
              icon: { type: 'icon', value: 'team' }
            },
            {
              text: this.translateService.instant('SIDEBAR.organizations'),
              link: '/admin/organizations',
              icon: { type: 'icon', value: 'bank' }
            },
            {
              text: this.translateService.instant('SIDEBAR.classes'),
              link: '/admin/document-classes',
              icon: { type: 'icon', value: 'database' }
            },
            {
              text: 'Klassenübersicht',
              link: '/admin/document-class-graph',
              icon: { type: 'icon', value: 'apartment' },
              acl: {
                role: [PlatformRole.Developer]
              }
            }
          ]
        },
        {
          text: 'Plattform',
          children: [
            {
              text: 'Anleitung',
              externalLink: '/docs',
              target: '_blank',
              icon: { type: 'icon', value: 'question-circle' }
            },
            ...(environment.platform_menu_entries ?? []).map(
              (e: PlatformMenuEntry) =>
                ({
                  text: e.label,
                  target: '_blank',
                  externalLink: e.forward_token
                    ? `${e.url}/?${new URLSearchParams({
                        auth_token: token || ''
                      }).toString()}`
                    : e.url,
                  icon: { type: 'icon', value: e.icon },
                  acl:
                    e.platform_roles || e.organization_roles
                      ? {
                          role: concat(
                            (e.platform_roles as string[] | undefined) || [],
                            (e.organization_roles as string[] | undefined) || []
                          )
                        }
                      : undefined
                } as any)
            ),
            {
              text: 'Grafana',
              externalLink: `${environment.api.baseUrl}${environment['grafana_url']}/?${new URLSearchParams({
                auth_token: token || ''
              }).toString()}`,
              target: '_blank',
              icon: { type: 'icon', value: 'line-chart' },
              acl: {
                role: environment['grafana_url'] ? [PlatformRole.Developer, PlatformRole.PlatformAdmin] : []
              }
            },
            {
              text: 'GraphiQL data',
              externalLink: `${environment.api.baseUrl}${environment['playground_url']}/?${new URLSearchParams({
                endpoint: '/graphql',
                token: token || ''
              }).toString()}`,
              target: '_blank',
              icon: { type: 'icon', value: 'play-circle' },
              acl: {
                role: [PlatformRole.Developer]
              }
            },
            {
              text: 'GraphiQL ui-data',
              externalLink: `${environment.api.baseUrl}${environment['playground_url']}/?${new URLSearchParams({
                endpoint: '/graphql-frontend',
                token: token || ''
              }).toString()}`,
              target: '_blank',
              icon: { type: 'icon', value: 'play-circle' },
              acl: {
                role: [PlatformRole.Developer]
              }
            },
            {
              text: 'GraphiQL history',
              externalLink: `${environment.api.baseUrl}${environment['playground_url']}/?${new URLSearchParams({
                endpoint: '/graphql-history',
                token: token || ''
              }).toString()}`,
              target: '_blank',
              icon: { type: 'icon', value: 'play-circle' },
              acl: {
                role: [PlatformRole.Developer]
              }
            }
          ]
        }
        // {
        //   text: 'Support',
        //   children: [
        //     {
        //       text: 'Support',
        //       link: '/help',
        //       icon: { type: 'icon', value: 'comment' },
        //     },
        //   ],
        //   group: false,
        // },
      ]);
    });

    this.auth.scope$
      .pipe(
        switchMap(scope =>
          merge(
            this.documentService.deletedOneDocument.subscribe({
              filter: {
                scope: { eq: scope }
              }
            }),
            this.documentService.updatedOneDocument$,
            this.documentService.createdDocument.subscribe({
              filter: {
                scope: { eq: scope }
              }
            })
          ).pipe(
            startWith([]),
            bufferTime(2000),
            startWith(['x']), // Make sure it loads fast on the first load
            filter(res => res.length > 0),
            filter(() => {
              return this.auth.token != undefined;
            }),
            mergeMap(res =>
              this.documentService.getAllDocumentCounts
                .mutate({ scope: scope! })
                .pipe(map(res => res.data?.getAllDocumentCounts))
                .pipe(catchError(() => of([])))
            )
          )
        ),
        tap(counts => {
          counts?.forEach(entry => {
            this.menuService.setItem(entry.regex ?? 'inbox', {
              badge: entry.count,
              badge_tag: entry.tag_count,
              badge_snooze: entry.snoozed_count
            });
          });
        })
      )
      .subscribe();

    // Can be set page suffix title, https://ng-alain.com/theme/title
    this.titleService.suffix = app.name ?? '';

    return of();
  }
}
